import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import IdMigrationDialog from "../../../molecules/IdMigrationDialog";

// Type definitions for props
export interface DeleteDoctorDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  doctorName?: string;
  action: "delete" | "removeFromClinic";
}

export interface ErrorDialogProps {
  open: boolean;
  onClose: () => void;
  errorMessage: string | null;
}

export interface EditDoctorDialogProps {
  open: boolean;
  onClose: () => void;
  doctorToEdit: any;
  onInputChange: (e: React.ChangeEvent<HTMLElement>) => void;
  onSave: () => void;
  /** Called after a successful LANR add/change so the parent can refresh its local copy */
  onLANRMigrated?: (newLANR: string) => void;
}

export interface EditClinicDialogProps {
  open: boolean;
  onClose: () => void;
  clinicToEdit: any;
  onInputChange: (e: React.ChangeEvent<HTMLElement> | any) => void;
  onSave: () => void;
  ReportMethod: any;
  /** Called after a successful BSNR add/change so the parent can refresh its local copy */
  onBSNRMigrated?: (newBSNR: string) => void;
}

export interface DeleteClinicDialogProps {
  open: boolean;
  onClose: () => void;
  onDelete: () => void;
}

// Delete Doctor Dialog
export const DeleteDoctorDialog = ({
  open,
  onClose,
  onConfirm,
  doctorName,
  action,
}: DeleteDoctorDialogProps) => {
  const isRemove = action === "removeFromClinic";
  const title = isRemove ? "Von Klinik entfernen" : "Arzt löschen";
  const message = isRemove
    ? <>Möchten Sie den Arzt <strong>{doctorName || ""}</strong> wirklich von dieser Klinik entfernen?</>
    : <>Möchten Sie den Arzt <strong>{doctorName || ""}</strong> wirklich löschen?</>;
  const confirmLabel = isRemove ? "Von Klinik entfernen" : "Arzt löschen";

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <p>{message}</p>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Abbrechen
        </Button>
        <Button onClick={onConfirm} color="secondary" variant="contained">
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Error Dialog
export const ErrorDialog = ({ open, onClose, errorMessage }: ErrorDialogProps) => (
  <Dialog open={open} onClose={onClose}>
    <DialogTitle>Fehler</DialogTitle>
    <DialogContent>
      <p>{errorMessage}</p>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} color="primary">
        Schließen
      </Button>
    </DialogActions>
  </Dialog>
);

// Edit Doctor Dialog
export const EditDoctorDialog = ({
  open,
  onClose,
  doctorToEdit,
  onInputChange,
  onSave,
  onLANRMigrated,
}: EditDoctorDialogProps) => {
  const [migrationOpen, setMigrationOpen] = useState(false);
  const currentLANR: string | null = doctorToEdit?.LANR || null;

  return (
  <Dialog open={open} onClose={onClose}>
    <DialogTitle>Arzt bearbeiten</DialogTitle>
    <DialogContent>
      {doctorToEdit ? (
        <div>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TextField
                  label="LANR"
                  name="LANR"
                  value={currentLANR || ""}
                  fullWidth
                  margin="normal"
                  disabled={true}
                  InputProps={{ style: { height: "50px" } }}
                />
                {doctorToEdit.id && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setMigrationOpen(true)}
                    startIcon={currentLANR ? <EditIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                    sx={{ textTransform: "none", whiteSpace: "nowrap", minWidth: "auto" }}
                  >
                    {currentLANR ? "LANR ändern" : "LANR hinzufügen"}
                  </Button>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Salutation"
                name="salutation"
                value={doctorToEdit.salutation || ""}
                onChange={onInputChange}
                fullWidth
                margin="normal"
                InputProps={{ style: { height: "50px" } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="First Name(s)"
                name="firstNames"
                value={doctorToEdit.firstNames || ""}
                onChange={onInputChange}
                fullWidth
                margin="normal"
                InputProps={{ style: { height: "50px" } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Last Name"
                name="lastName"
                value={doctorToEdit.lastName || ""}
                onChange={onInputChange}
                fullWidth
                margin="normal"
                InputProps={{ style: { height: "50px" } }}
              />
            </Grid>
          </Grid>
        </div>
      ) : (
        <p>Kein Arzt zum Bearbeiten ausgewählt.</p>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} color="primary">
        Abbrechen
      </Button>
      <Button onClick={onSave} color="primary">
        Speichern
      </Button>
    </DialogActions>

    <IdMigrationDialog
      open={migrationOpen}
      onClose={() => setMigrationOpen(false)}
      type="doctor"
      entityId={doctorToEdit?.id || ""}
      currentValue={currentLANR}
      onMigrationComplete={(newValue) => {
        onLANRMigrated?.(newValue);
      }}
    />
  </Dialog>
  );
};

// Edit Clinic Dialog
export const EditClinicDialog = ({
  open,
  onClose,
  clinicToEdit,
  onInputChange,
  onSave,
  ReportMethod,
  onBSNRMigrated,
}: EditClinicDialogProps) => {
  const [migrationOpen, setMigrationOpen] = useState(false);
  const currentBSNR: string | null = clinicToEdit?.BSNR || null;
  // Use the entity UUID for migration; fall back to id (works for both legacy IDs and UUIDs)
  const migrationEntityId: string = clinicToEdit?.entityId || clinicToEdit?.id || "";

  return (
  <Dialog open={open} onClose={onClose}>
    <DialogTitle>Praxis bearbeiten</DialogTitle>
    <DialogContent>
      {clinicToEdit ? (
        <div>
          <Grid container spacing={2}>
            <Grid item xs={12} style={{marginTop: "1rem"}}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TextField
                  label="BSNR"
                  name="BSNR"
                  value={currentBSNR || ""}
                  style={{ marginTop: 0, marginBottom: 0 }}
                  fullWidth
                  margin="normal"
                  disabled={true}
                  InputProps={{ style: { height: "50px" } }}
                />
                {migrationEntityId && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setMigrationOpen(true)}
                    startIcon={currentBSNR ? <EditIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                    sx={{ textTransform: "none", whiteSpace: "nowrap", minWidth: "auto" }}
                  >
                    {currentBSNR ? "BSNR ändern" : "BSNR hinzufügen"}
                  </Button>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="BusinessName"
                name="businessName"
                value={clinicToEdit.businessName || ""}
                style={{ marginTop: 0, marginBottom: 0 }}
                onChange={onInputChange}
                fullWidth
                margin="normal"
                InputProps={{ style: { height: "50px" } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Telefon"
                name="phone1"
                value={clinicToEdit.phone1 || ""}
                style={{ marginTop: 0, marginBottom: 0 }}
                onChange={onInputChange}
                fullWidth
                margin="normal"
                InputProps={{ style: { height: "50px" } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Fax"
                name="fax"
                value={clinicToEdit.fax || ""}
                style={{ marginTop: 0, marginBottom: 0 }}
                onChange={onInputChange}
                fullWidth
                margin="normal"
                InputProps={{ style: { height: "50px" } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address Line 1"
                name="addressLine1"
                value={clinicToEdit.addressLine1 || ""}
                style={{ marginTop: 0, marginBottom: 0 }}
                onChange={onInputChange}
                fullWidth
                margin="normal"
                InputProps={{ style: { height: "50px" } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Address Line 2"
                name="addressLine2"
                value={clinicToEdit.addressLine2 || ""}
                style={{ marginTop: 0, marginBottom: 0 }}
                onChange={onInputChange}
                fullWidth
                margin="normal"
                InputProps={{ style: { height: "50px" } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="City"
                name="city"
                value={clinicToEdit.city || ""}
                style={{ marginTop: 0, marginBottom: 0 }}
                onChange={onInputChange}
                fullWidth
                margin="normal"
                InputProps={{ style: { height: "50px" } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Zip"
                name="zip"
                value={clinicToEdit.zip || ""}
                style={{ marginTop: 0, marginBottom: 0 }}
                onChange={onInputChange}
                fullWidth
                margin="normal"
                InputProps={{ style: { height: "50px" } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email"
                name="email"
                value={clinicToEdit.email || ""}
                style={{ marginTop: 0, marginBottom: 0 }}
                onChange={onInputChange}
                fullWidth
                margin="normal"
                InputProps={{ style: { height: "50px" } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Hinweise"
                name="notes"
                value={clinicToEdit.notes || ""}
                style={{ marginTop: 0, marginBottom: 0 }}
                onChange={onInputChange}
                fullWidth
                margin="normal"
                InputProps={{ style: { height: "50px" } }}
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel>Methode für Therapiebericht</InputLabel>
              <Select
                name="methodForDrReport"
                value={clinicToEdit.methodForDrReport || ReportMethod.Email}
                onChange={onInputChange}
                fullWidth
              >
                {Object.values(ReportMethod).map((method) => (
                  <MenuItem key={String(method)} value={method as string}>
                    {String(method)}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12}>
              <InputLabel>Methode für Rezeptbestellung</InputLabel>
              <Select
                name="methodForRxOrder"
                value={clinicToEdit.methodForRxOrder || ReportMethod.Email}
                onChange={onInputChange}
                fullWidth
              >
                {Object.values(ReportMethod).map((method) => (
                  <MenuItem key={String(method)} value={method as string}>
                    {String(method)}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
          </Grid>
        </div>
      ) : (
        <p>Keine Praxis zum Bearbeiten ausgewählt.</p>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose} color="primary">
        Abbrechen
      </Button>
      <Button onClick={onSave} color="primary">
        Speichern
      </Button>
    </DialogActions>

    <IdMigrationDialog
      open={migrationOpen}
      onClose={() => setMigrationOpen(false)}
      type="clinic"
      entityId={migrationEntityId}
      currentValue={currentBSNR}
      onMigrationComplete={(newValue) => {
        onBSNRMigrated?.(newValue);
      }}
    />
  </Dialog>
  );
};

// Delete Clinic Dialog
export const DeleteClinicDialog = ({ open, onClose, onDelete }: DeleteClinicDialogProps) => (
  <Dialog open={open} onClose={onClose}>
    <DialogTitle>Löschen bestätigen</DialogTitle>
    <DialogContent>Möchten Sie diese Praxis wirklich löschen?</DialogContent>
    <DialogActions>
      <Button onClick={onClose} color="primary">
        Abbrechen
      </Button>
      <Button onClick={onDelete} color="primary">
        Praxis löschen
      </Button>
    </DialogActions>
  </Dialog>
);
